import React from "react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  Button,
  Field,
  FieldLabel,
  Input,
} from "@salestrak/ui";
import { useRouter } from '#app/lib/hooks/router-compat';
import { useAuth } from "#app/lib/auth-context";
import { PASSWORD_MIN_LENGTH } from "#app/lib/auth-service";
import { useAnalytics, AnalyticsContext, AuthEvent } from "#app/lib/analytics";
import { GoogleSignInButton } from "./google-sign-in-button";

export function RegisterForm() {
  const { signUp } = useAuth();
  const { track } = useAnalytics();
  const router = useRouter();
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [googlePending, setGooglePending] = React.useState(false);

  const handleRegister = async () => {
    if (submitting || googlePending) return;
    setError("");
    if (!name.trim()) {
      setError("Full name is required.");
      return;
    }
    if (!email.trim()) {
      setError("Email is required.");
      return;
    }
    if (!password.trim()) {
      setError("Password is required.");
      return;
    }
    if (password.length < PASSWORD_MIN_LENGTH) {
      setError(`Password must be at least ${PASSWORD_MIN_LENGTH} characters.`);
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await signUp(name.trim(), email.trim(), password);
      track({ context: AnalyticsContext.Auth, event: AuthEvent.SignUp, label: "success" });
      // The account starts email-unverified; sign-in happens through the
      // emailed verification link, so hand off to the check-your-email screen.
      router.replace({
        pathname: "/(auth)/verify-email-sent",
        params: { email: email.trim() },
      });
    } catch (err: any) {
      track({ context: AnalyticsContext.Auth, event: AuthEvent.SignUp, label: "failure" });
      setError(err.message ?? "Registration failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // The form owns submission — Enter from any field registers.
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void handleRegister();
  };

  return (
    <Box as="form" direction="column" gap="xl" onSubmit={handleSubmit}>
      <GoogleSignInButton
        onError={setError}
        disabled={submitting}
        onPendingChange={setGooglePending}
      />

      <Box direction="column" gap="sm">
        <Field>
          <FieldLabel>Full Name</FieldLabel>
          <Input
            placeholder="John Smith"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            autoComplete="name"
            disabled={submitting || googlePending}
          />
        </Field>

        <Field>
          <FieldLabel>Email</FieldLabel>
          <Input
            type="email"
            placeholder="name@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            disabled={submitting || googlePending}
          />
        </Field>

        <Field>
          <FieldLabel>Password</FieldLabel>
          <Input
            type="password"
            placeholder={`At least ${PASSWORD_MIN_LENGTH} characters`}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            disabled={submitting || googlePending}
          />
        </Field>

        <Field>
          <FieldLabel>Confirm Password</FieldLabel>
          <Input
            type="password"
            placeholder="Re-enter your password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            disabled={submitting || googlePending}
          />
        </Field>
      </Box>

      {error ? (
        <Alert variant="error">
          <AlertTitle>Unable to create account</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Button
        type="submit"
        loading={submitting}
        disabled={
          !name.trim() ||
          !email.trim() ||
          !password.trim() ||
          password.length < PASSWORD_MIN_LENGTH ||
          !confirmPassword.trim() ||
          googlePending
        }
      >
        Create Account
      </Button>
    </Box>
  );
}
